import { IconType } from 'components/atoms/Icon';
import { StageCandidateActionType } from 'enums/stageAction';
import { AdjustmentQuestionnaireResponse } from 'generated/graphql';

const untimedTests = [
  StageCandidateActionType.PersonalityTest,
  StageCandidateActionType.RssAssessment,
  StageCandidateActionType.DeiForm,
  StageCandidateActionType.Form,
];

export type steps = 'intro' | 'options' | 'summary' | 'details' | '';

/**
 * Routing seam for the adjustments flow: the same steps are reachable from
 * the legacy /stages flow and the new /roles flow, so containers inject
 * where each step lives and where the flow exits to.
 */
export type TAdjustmentsFlowRoutes = {
  getStepUrl: (step: Exclude<steps, ''>) => string;
  exitUrl: string;
};

export type adjustmentsResponsesType = AdjustmentQuestionnaireResponse[];

export const hasAuditoryAdjustment = (responses: adjustmentsResponsesType) =>
  responses.includes(AdjustmentQuestionnaireResponse.AuditoryProcessing);

export const isUntimedTest = (test: StageCandidateActionType) =>
  untimedTests.includes(test);

export const hasOnlyUntimedAssessments = (tests: StageCandidateActionType[]) =>
  tests.every(test => isUntimedTest(test));

const hasOnlyAuditoryAdjustment = (responses: adjustmentsResponsesType) =>
  responses.length === 1 && hasAuditoryAdjustment(responses);

export const requiresAdjustments = (
  responses: adjustmentsResponsesType,
  tests: StageCandidateActionType[]
) => {
  // The AI interview is timed but spoken, so it is judged on its own
  // entitlements: the auditory answer that buys nothing on a written test does
  // buy extra time here, and the assistive-technology answer buys none.
  const aiInterviewQualifies =
    tests.includes(StageCandidateActionType.AiInterviews) &&
    getAiInterviewExtraTimePercentage(responses) > 0;

  const otherTests = tests.filter(
    test => test !== StageCandidateActionType.AiInterviews
  );
  // If user has only un-timed tests or only auditory requirements, we cannot offer adjustments
  const otherTestsQualify = !(
    hasOnlyUntimedAssessments(otherTests) || hasOnlyAuditoryAdjustment(responses)
  );

  return aiInterviewQualifies || otherTestsQualify;
};

export type LabelsType = Exclude<
  StageCandidateActionType,
  StageCandidateActionType.TalentReviewTest
>;

export const mapTestTypeToLabel: Record<LabelsType, string> = {
  [StageCandidateActionType.PersonalityTest]: 'personality',
  [StageCandidateActionType.CognitiveInductiveTest]: 'inductive',
  [StageCandidateActionType.CognitiveNumericalTest]: 'numerical',
  [StageCandidateActionType.CognitiveVerablTest]: 'verbal',
  [StageCandidateActionType.CodingExternalAssessment]: 'coderbyte',
  [StageCandidateActionType.EnglishProficiencyTest]: 'englishProficiency',
  [StageCandidateActionType.RssAssessment]: 'selfAssessment',
  [StageCandidateActionType.DeiForm]: 'dei',
  [StageCandidateActionType.Form]: 'form',
  [StageCandidateActionType.AiInterviews]: 'aiInterview',
};

export const mapTestToIcon: Record<LabelsType, IconType> = {
  [StageCandidateActionType.PersonalityTest]: 'personality_test',
  [StageCandidateActionType.CognitiveInductiveTest]: 'inductive_test',
  [StageCandidateActionType.CognitiveNumericalTest]: 'numerical_test',
  [StageCandidateActionType.CognitiveVerablTest]: 'verbal_test',
  [StageCandidateActionType.CodingExternalAssessment]: 'cog',
  [StageCandidateActionType.EnglishProficiencyTest]: 'language',
  [StageCandidateActionType.RssAssessment]: 'skills_self_assessment',
  [StageCandidateActionType.DeiForm]: 'dei',
  [StageCandidateActionType.Form]: 'questions',
  [StageCandidateActionType.AiInterviews]: 'brain',
};

export const getTimeAmountAdded = (responses: adjustmentsResponsesType) => {
  // Extra time will be the most amount possible based on the adjustments needed.
  // For example, if 3 responses are selected and one of them is Assistive Technology
  // then the user will receive the most amount of extra time possible (50%) regardless
  // what the other responses are.
  let extraTime = 25;

  if (responses.includes(AdjustmentQuestionnaireResponse.PhysicalChallenges)) {
    extraTime = 35;
  }

  if (responses.includes(AdjustmentQuestionnaireResponse.AssistiveTechnology)) {
    extraTime = 50;
  }

  return `+${extraTime}%`;
};

/**
 * Extra time an AI interview earns, per the Reasonable Adjustments (Timing)
 * spec. Mirrors ADJUSTMENT_QUESTIONNAIRE_AI_INTERVIEW_TIME_MODIFIER_VALUES in
 * the monolith, which is what actually gets saved against the candidate —
 * keep the two in step. The percentages differ from the written-test ones
 * above because the timed part of an interview is a spoken conversation:
 * reading, motor and screen-reader demands fall away, while a difficulty with
 * speaking or with hearing the interviewer is squarely in the way.
 */
const AI_INTERVIEW_EXTRA_TIME_PERCENTAGES: Record<
  AdjustmentQuestionnaireResponse,
  number
> = {
  [AdjustmentQuestionnaireResponse.AssistiveTechnology]: 0,
  [AdjustmentQuestionnaireResponse.PhysicalChallenges]: 0,
  [AdjustmentQuestionnaireResponse.DifficultyProcessingInformation]: 0,
  [AdjustmentQuestionnaireResponse.DifficultyMaintainingFocus]: 50,
  [AdjustmentQuestionnaireResponse.DifficultyManagingAnxiety]: 50,
  [AdjustmentQuestionnaireResponse.DifficultyManagingFatigue]: 50,
  [AdjustmentQuestionnaireResponse.AuditoryProcessing]: 50,
  [AdjustmentQuestionnaireResponse.DifficultySpokenCommunication]: 50,
};

export const getAiInterviewExtraTimePercentage = (
  responses: adjustmentsResponsesType
) =>
  responses.reduce(
    (largest, response) =>
      Math.max(largest, AI_INTERVIEW_EXTRA_TIME_PERCENTAGES[response] ?? 0),
    0
  );

/**
 * The AI interview's extra-time label, or null when these responses earn none
 * — the caller then has nothing to show rather than a "+0%" tag.
 */
export const getAiInterviewTimeAmountAdded = (
  responses: adjustmentsResponsesType
) => {
  const percentage = getAiInterviewExtraTimePercentage(responses);
  return percentage > 0 ? `+${percentage}%` : null;
};

export const stepsOrder = ['intro', 'options', 'summary', 'details'];

export const shouldRedirectToIntro = (completedStep: steps) => {
  return completedStep === '';
};


